import { useEffect, useId, useRef, useState } from 'react';
import styles from './accordion.module.scss';
import { Icon } from '../icons/Icon';
import { Image } from '../image/Image';
import { AccordionProps } from '@msc-b2c/interfaces/types';
import {
  getEditorialAccordionCloseIconLabel,
  getEditorialAccordionOpenIconLabel,
  getOverwriteExistingLabelsForAccordionLabels,
  handleKeyDown,
  sanitize,
  setReactAccAttributes,
  stripHtml,
} from '@msc-b2c/shared-utils';
import { Text } from '../text/Text';

export const Accordion = ({
  question,
  idFaq,
  answer,
  isOpen,
  onToggle,
  viewDetailsLabel,
  autoClose,
  openingIcon,
  openingAriaLabel,
  closingIcon,
  closingAriaLabel,
  questionIcon,
  questionImage,
  placeholderImagePath,
  questionIconAriaLabel,
  children,
  multi,
  styleOption,
  status,
  additionalDetailsLabel,
  iconLabelNoUnderline,
  subtitle,
}: AccordionProps) => {
  // Local state to manage the open/close status of the accordion.
  const [isOpenLocal, setIsOpenLocal] = useState(isOpen);

  // Ref to the accordion element for scrolling
  const accordionRef = useRef<HTMLDivElement>(null);
  // Ref to track previous open state to detect closing transition
  const prevIsOpenRef = useRef(isOpenLocal);
  const generatedAnswerId = useId();
  const answerId = idFaq ? `${idFaq}-answer` : generatedAnswerId;

  // Effect to synchronize the local state with the external state when autoClose is enabled.
  useEffect(() => {
    if (autoClose) {
      setIsOpenLocal(isOpen); // Synchronize local state with the prop `isOpen` when `autoClose` is true.
    }
  }, [isOpen, autoClose]);

  useEffect(() => {
    // If the accessibility form accordion is done, close it
    if (status && status === 'done') {
      setIsOpenLocal(false);
    }
  }, [status]);

  useEffect(() => {
    // Scroll to accordion when it closes due to status being 'done'
    if (status === 'done' && prevIsOpenRef.current && !isOpenLocal && accordionRef.current) {
      accordionRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
    prevIsOpenRef.current = isOpenLocal;
  }, [isOpenLocal, status]);

  // Handler function to toggle the accordion's open/close status.
  const handleOpenAccordion: () => void = () => {
    if (autoClose) {
      onToggle(); // If `autoClose` is enabled, use the centralized state management in the parent component.
    } else {
      setIsOpenLocal(!isOpenLocal); // Otherwise, manage the open/close state locally.
    }
  };

  const triggerAccessibleName = [
    questionIconAriaLabel,
    question ? stripHtml(question).trim() : '',
    subtitle,
    additionalDetailsLabel,
    viewDetailsLabel,
    iconLabelNoUnderline,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      ref={accordionRef}
      className={`${styles['accordion__cmp']}${
        isOpenLocal ? ' '.concat(styles['accordion__cmp--active']) : ''
      } ${styleOption ?? ''}`}
    >
      <div
        onClick={handleOpenAccordion}
        className={styles['accordion__cmp-question']}
        id={idFaq}
        onKeyDown={(event) => handleKeyDown(event, handleOpenAccordion)}
        tabIndex={0}
        role="button"
        aria-expanded={isOpenLocal}
        aria-controls={answerId}
        aria-label={triggerAccessibleName || undefined}
      >
        <div className={`${styles['accordion__cmp-question-wrapper']}`}>
          {questionIcon && (
            <Icon
              icon={questionIcon}
              ariaLabel={questionIconAriaLabel}
              styleOption={`${styles['accordion__cmp-question-icon']}`}
            />
          )}
          {questionImage && (
            <Image
              src={questionImage}
              placeholderImagePath={placeholderImagePath}
              alt={question ? stripHtml(question).trim() : ''}
              styleOption={`${styles['accordion__cmp-question-wrapper--image']}`}
            />
          )}
          <Text
            text={question}
            tagHtml="span"
            styleOption={`${styles['accordion__cmp-question-title']}`}
          />
          {!!subtitle && (
            <Text
              text={subtitle}
              tagHtml="span"
              styleOption={`cmp-text__normal ${styles['accordion__cmp-question-subtitle']}`}
            />
          )}
        </div>
        <div className={`${styles['accordion__cmp-toggle-wrapper']}`}>
          {status && (
            <Icon
              icon={'check'}
              ariaLabel={status}
            />
          )}
          {additionalDetailsLabel && (
            <Text
              text={additionalDetailsLabel}
              tagHtml="span"
              styleOption={`body-default ${styles['accordion__cmp-additional-details-label']}`}
            />
          )}
          {viewDetailsLabel && (
            <Text
              text={viewDetailsLabel}
              tagHtml="span"
              styleOption={`${styles['accordion__cmp-view-details-label']}`}
            />
          )}
          {iconLabelNoUnderline && (
            <Text
              text={iconLabelNoUnderline}
              tagHtml="span"
              styleOption={`${styles['accordion__cmp-icon-label-no-underline']}`}
            />
          )}
          <Icon
            icon={isOpenLocal ? closingIcon : openingIcon}
            ariaLabel={isOpenLocal ? openingAriaLabel : closingAriaLabel}
            onClick={(event) => {
              event.stopPropagation();
              globalThis.getSelection()?.removeAllRanges();
              handleOpenAccordion();
            }}
            tooltipTitle={
              isOpenLocal
                ? setReactAccAttributes(
                    getEditorialAccordionCloseIconLabel(),
                    closingAriaLabel ?? '',
                    getOverwriteExistingLabelsForAccordionLabels()
                  )
                : setReactAccAttributes(
                    getEditorialAccordionOpenIconLabel(),
                    openingAriaLabel ?? '',
                    getOverwriteExistingLabelsForAccordionLabels()
                  )
            }
            styleOption={`${styles['accordion__cmp-toggle-icon']}`}
          />
        </div>
      </div>
      <div
        id={answerId}
        className={`${styles['accordion__cmp-answer']} ${isOpenLocal ? 'open' : ''}`}
        aria-hidden={!isOpenLocal}
        tabIndex={multi ? undefined : isOpenLocal ? 0 : -1}
        {...(!isOpenLocal ? { inert: '' } : {})}
      >
        {answer && <span dangerouslySetInnerHTML={{ __html: sanitize(answer) }}></span>}
        {multi && children}
      </div>
    </div>
  );
};
